'use client'

import { useResizeObserver } from '@react-hookz/web'

import { useCallback, useRef, useState } from 'react'

import throttle from 'lodash/fp/throttle'

// Edge has a bug where scrollHeight is 1px bigger than clientHeight when there's no scroll.
const isEdge =
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  /edge\/\d./i.test(window.navigator.userAgent)

type StateValue = {
  className?: string
  direction?: number
  percentage?: number | null
  total?: number
  value?: number
}
type State = {
  x: StateValue
  y: StateValue
}
export default function useScrollInfo(throttleTime = 50) {
  const [scroll, setScroll] = useState<State>({ x: {}, y: {} })
  const ref = useRef<HTMLElement | null>(null)
  const previousScroll = useRef<State>({ x: {}, y: {} })

  const update = () => {
    const element = ref.current
    if (element === null) {
      return
    }

    let maxY = element.scrollHeight - element.clientHeight
    const maxX = element.scrollWidth - element.clientWidth

    // Edge has a bug where scrollHeight is 1px bigger than clientHeight when there's no scroll.
    if (isEdge && maxY === 1 && element.scrollTop === 0) {
      maxY = 0
    }

    const percentageY = maxY === 0 ? null : element.scrollTop / maxY
    const percentageX = maxX === 0 ? null : element.scrollLeft / maxX

    let classNameY = 'no-scroll-y'
    if (percentageY === 0) {
      classNameY = 'scroll-top'
    } else if (percentageY === 1) {
      classNameY = 'scroll-bottom'
    } else if (percentageY) {
      classNameY = 'scroll-middle-y'
    }

    let classNameX = 'no-scroll-x'
    if (percentageX === 0) {
      classNameX = 'scroll-left'
    } else if (percentageX === 1) {
      classNameX = 'scroll-right'
    } else if (percentageX) {
      classNameX = 'scroll-middle-x'
    }

    const previous = previousScroll.current

    const scrollInfo = {
      x: {
        className: classNameX,
        direction: previous
          ? Math.sign(element.scrollLeft - (previous.x.value ?? 0))
          : 0,
        percentage: percentageX,
        total: maxX,
        value: element.scrollLeft,
      },
      y: {
        className: classNameY,
        direction: previous
          ? Math.sign(element.scrollTop - (previous.y.value ?? 0))
          : 0,
        percentage: percentageY,
        total: maxY,
        value: element.scrollTop,
      },
    }
    previousScroll.current = scrollInfo
    setScroll(scrollInfo)
  }

  const throttledUpdate = throttle(throttleTime, update)

  useResizeObserver(ref, throttledUpdate)

  const setRef = useCallback((node: HTMLElement | null) => {
    if (typeof window === 'undefined') {
      return
    }

    if (node) {
      // When the ref is first set (after mounting).
      node.addEventListener('scroll', throttledUpdate)
      if (!window.ResizeObserver) {
        // Fallback if ResizeObserver is not available
        window.addEventListener('resize', throttledUpdate)
      }

      ref.current = node
      throttledUpdate()
    } else if (ref.current) {
      // When unmounting
      ref.current.removeEventListener('scroll', throttledUpdate)
      if (!window.ResizeObserver) {
        window.removeEventListener('resize', throttledUpdate)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return [scroll, setRef, ref] as const
}
